/**
 * Compose + preview dialog for sending an invoice or estimate to a customer.
 *
 * Replaces the old one-click Send (which fired instantly with a server-
 * generated body and no recipient choice). On open it fetches the real draft
 * the server would send (`GET /api/{invoices|estimates}/:id/email-draft`) so
 * the office can review and edit the To list, subject, and body before it goes
 * out. On send it POSTs/PATCHes the edited values; the server delivers exactly
 * what is shown (the PDF + work-order attachments are always added server-side).
 */
import { BudgetImpactLine } from "@/pages/customers/budget/impact-line";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { AlertTriangle, Loader2, Mail, Paperclip, Plus, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

type DocType = "invoice" | "estimate";

interface EmailDraft {
    recipients: string[];
    subject: string;
    body: string;
    pdfAttached: boolean;
    workOrdersAttached?: number;
}

interface ContactMethod {
    id: string;
    kind: string;
    value: string;
    label: string | null;
}

interface ContactWithMethods {
    id: string;
    firstName: string | null;
    lastName: string | null;
    roleTitle: string | null;
    methods: ContactMethod[];
}

interface SendResponse {
    emailed?: boolean;
    to?: string | null;
    send_errors?: string[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function contactDisplayName(c: ContactWithMethods): string {
    const name = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
    return name || "Unnamed contact";
}

export function SendCustomerDialog({
    docType,
    id,
    customerId,
    open,
    onOpenChange,
    onSent,
}: {
    docType: DocType;
    id: string;
    customerId: string | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSent?: () => void;
}) {
    const qc = useQueryClient();
    const label = docType === "invoice" ? "Invoice" : "Estimate";

    const draftPath = `/api/${docType === "invoice" ? "invoices" : "estimates"}/${id}/email-draft`;
    const draft = useQuery({
        queryKey: ["email-draft", docType, id],
        queryFn: () => api.get<EmailDraft>(draftPath),
        enabled: open && !!id,
        staleTime: 0,
    });

    const contactsQuery = useQuery({
        queryKey: ["customer-contact-methods", customerId],
        queryFn: () => api.get<{ contacts: ContactWithMethods[] }>(`/api/customers/${customerId}/contact-methods`),
        enabled: open && !!customerId,
    });

    const [recipients, setRecipients] = useState<string[]>([]);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [seeded, setSeeded] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    // Per-send block toggle (Prompt 4 Part B §6): dropping the completed-WO
    // rider affects THIS send only — the recipe default is untouched.
    const [includeWorkOrders, setIncludeWorkOrders] = useState(true);
    // TAX ENGINE V1: server refused the send with TAX_STATUS_UNKNOWN — show
    // the amber interstitial with one-click taxable/exempt set-and-continue.
    const [taxPrompt, setTaxPrompt] = useState<{ customerId: string } | null>(null);
    const [taxResolving, setTaxResolving] = useState<"taxable" | "exempt" | null>(null);

    // Seed editable fields from the fetched draft once.
    useEffect(() => {
        if (!draft.data || seeded) return;
        setRecipients(draft.data.recipients ?? []);
        setSubject(draft.data.subject ?? "");
        setBody(draft.data.body ?? "");
        setSeeded(true);
    }, [draft.data, seeded]);

    // Reset local state whenever the dialog closes so reopening re-seeds fresh.
    useEffect(() => {
        if (open) return;
        setSeeded(false);
        setShowAdd(false);
        setNewEmail("");
        setIncludeWorkOrders(true);
        setTaxPrompt(null);
        setTaxResolving(null);
    }, [open]);

    const addRecipient = (value: string) => {
        const v = value.trim();
        if (!EMAIL_RE.test(v)) return;
        setRecipients((prev) => (prev.some((r) => r.toLowerCase() === v.toLowerCase()) ? prev : [...prev, v]));
    };

    const removeRecipient = (value: string) => {
        setRecipients((prev) => prev.filter((r) => r.toLowerCase() !== value.toLowerCase()));
    };

    const submitNewEmail = () => {
        addRecipient(newEmail);
        setNewEmail("");
        setShowAdd(false);
    };

    const sendMut = useMutation({
        // Optional overrides let the tax set-and-continue path resend with the
        // freshly regenerated subject/body without waiting for a state cycle.
        mutationFn: (overrides?: { subject?: string; body?: string }) => {
            const payload = {
                recipients,
                subject: overrides?.subject ?? subject,
                body: overrides?.body ?? body,
                ...(docType === "invoice" && (draft.data?.workOrdersAttached ?? 0) > 0 ? { includeWorkOrders } : {}),
            };
            return docType === "invoice"
                ? api.post<SendResponse>(`/api/invoices/${id}/send`, payload)
                : api.patch<SendResponse>(`/api/estimates/${id}/send`, payload);
        },
        onSuccess: (data) => {
            if (data?.send_errors && data.send_errors.length > 0) {
                toast.error(
                    `${label} could not be delivered: ${data.send_errors.join(", ")}. Check the email settings and try again.`,
                );
            } else if (data?.emailed === false) {
                toast.error(`${label} was not sent — no deliverable recipient.`);
            } else {
                toast.success(`${label} sent to ${recipients.join(", ")}`);
            }
            qc.invalidateQueries({ queryKey: [docType, id] });
            qc.invalidateQueries({ queryKey: ["email-draft", docType, id] });
            onSent?.();
            onOpenChange(false);
        },
        onError: (err) => {
            const errBody =
                err instanceof ApiError && err.body && typeof err.body === "object"
                    ? (err.body as { error?: unknown; message?: unknown; customerId?: unknown })
                    : null;
            // Tax status was never decided for this customer — swap the toast
            // for the amber interstitial (spec: never a silent $0-tax send).
            if (errBody?.error === "TAX_STATUS_UNKNOWN") {
                const cid = typeof errBody.customerId === "string" ? errBody.customerId : customerId;
                if (cid) {
                    setTaxPrompt({ customerId: cid });
                    return;
                }
            }
            const msg = errBody?.message ? String(errBody.message) : "";
            toast.error(msg || `Failed to send ${label.toLowerCase()}.`);
        },
    });

    /**
     * One-click set-and-continue from the tax interstitial: persist the tax
     * status (the server re-taxes the customer's draft docs), regenerate the
     * email draft so the totals/subject reflect the new tax line, then resend.
     */
    const resolveTaxAndSend = async (status: "taxable" | "exempt") => {
        if (!taxPrompt) return;
        setTaxResolving(status);
        try {
            await api.patch(`/api/customers/${taxPrompt.customerId}`, { taxStatus: status });
            const fresh = await api.get<EmailDraft>(draftPath);
            setSubject(fresh.subject ?? subject);
            setBody(fresh.body ?? body);
            setTaxPrompt(null);
            qc.invalidateQueries({ queryKey: ["email-draft", docType, id] });
            sendMut.mutate({ subject: fresh.subject ?? subject, body: fresh.body ?? body });
        } catch (err) {
            toast.error(err instanceof Error ? err.message : "Could not update the customer's tax status.");
        } finally {
            setTaxResolving(null);
        }
    };

    const emailContacts: ContactWithMethods[] = useMemo(
        () =>
            (contactsQuery.data?.contacts ?? [])
                .map((c) => ({ ...c, methods: c.methods.filter((m) => m.kind === "email") }))
                .filter((c) => c.methods.length > 0),
        [contactsQuery.data],
    );
    const alreadyAdded = new Set(recipients.map((r) => r.toLowerCase()));

    const workOrderCount = draft.data?.workOrdersAttached ?? 0;

    const attachmentNote = (() => {
        if (!draft.data) return null;
        const parts: string[] = [];
        if (draft.data.pdfAttached) parts.push(`the ${label.toLowerCase()} PDF`);
        if (workOrderCount > 0 && includeWorkOrders) {
            parts.push(`${workOrderCount} work order${workOrderCount === 1 ? "" : "s"}`);
        }
        if (parts.length === 0) return null;
        return `${parts.join(" and ")} will be attached automatically.`;
    })();

    return (
        <Dialog open={open} onClose={() => onOpenChange(false)} title={`Send ${label}`} className="max-w-2xl">
            {draft.isLoading ? (
                <div className="flex items-center gap-2 py-8 text-sm text-foreground/60">
                    <Loader2 className="h-4 w-4 animate-spin" /> Preparing draft…
                </div>
            ) : draft.isError ? (
                <div className="py-8 text-sm text-destructive">
                    Could not load the {label.toLowerCase()} draft. Close and try again.
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Recipients */}
                    <div className="space-y-1.5">
                        <Label className="flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5 text-foreground/70" /> To
                        </Label>
                        {recipients.length === 0 ? (
                            <p className="text-xs text-amber-600 dark:text-amber-400">
                                No recipient on file — add an address below or this won&apos;t be delivered.
                            </p>
                        ) : (
                            <ul className="flex flex-wrap gap-1.5">
                                {recipients.map((r) => (
                                    <li
                                        key={r}
                                        className="flex items-center gap-1.5 rounded bg-muted/50 px-2 py-1 text-sm"
                                    >
                                        <span className="break-all">{r}</span>
                                        <button
                                            type="button"
                                            className="text-muted-foreground hover:text-destructive"
                                            onClick={() => removeRecipient(r)}
                                            aria-label={`Remove ${r}`}
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="flex flex-wrap items-center gap-2 pt-0.5">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 gap-1 px-2 text-xs"
                                onClick={() => setShowAdd((v) => !v)}
                            >
                                <Plus className="h-3 w-3" /> Add recipient
                            </Button>
                        </div>
                        {showAdd && (
                            <div className="space-y-2 rounded border border-border/60 p-2">
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="email"
                                        placeholder="name@example.com"
                                        value={newEmail}
                                        onChange={(e) => setNewEmail(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                submitNewEmail();
                                            }
                                        }}
                                        className="h-8 text-sm"
                                    />
                                    <Button
                                        type="button"
                                        size="sm"
                                        className="h-8 px-2 text-xs"
                                        disabled={!EMAIL_RE.test(newEmail.trim())}
                                        onClick={submitNewEmail}
                                    >
                                        Add
                                    </Button>
                                </div>
                                {emailContacts.length > 0 && (
                                    <div className="max-h-40 space-y-1 overflow-y-auto border-t pt-2">
                                        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                            Saved contacts
                                        </p>
                                        {emailContacts.map((c) =>
                                            c.methods.map((m) => {
                                                const added = alreadyAdded.has(m.value.toLowerCase());
                                                return (
                                                    <button
                                                        key={m.id}
                                                        type="button"
                                                        disabled={added}
                                                        className={cn(
                                                            "flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm",
                                                            added
                                                                ? "cursor-not-allowed text-muted-foreground"
                                                                : "hover:bg-muted/60",
                                                        )}
                                                        onClick={() => addRecipient(m.value)}
                                                    >
                                                        <UserPlus className="h-3 w-3 text-foreground/50" />
                                                        <span className="break-all">{m.value}</span>
                                                        <span className="text-xs text-muted-foreground">
                                                            ({contactDisplayName(c)})
                                                        </span>
                                                        {added && <span className="ml-auto text-[10px]">added</span>}
                                                    </button>
                                                );
                                            }),
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Subject */}
                    <div className="space-y-1.5">
                        <Label htmlFor="send-subject">Subject</Label>
                        <Input
                            id="send-subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="text-sm"
                        />
                    </div>

                    {/* Body — the MESSAGE only. The greeting, the
                        Approve/Pay button, and the signature are chrome the
                        server re-applies on every send (#739), so they are
                        deliberately not in this box: editing them away used to
                        strip the button off the email entirely. */}
                    <div className="space-y-1.5">
                        <Label htmlFor="send-body">Message</Label>
                        <Textarea
                            id="send-body"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            rows={12}
                            className="text-sm font-mono leading-relaxed"
                        />
                        <p className="text-xs text-foreground/60">
                            Edit the message only. The greeting, your signature and branding, and the{" "}
                            {docType === "invoice" ? "payment" : "approval"} button are added automatically.
                        </p>
                    </div>

                    {/* Per-send block toggles (Prompt 4): the office can drop
                        the completed-WO rider for this send only. */}
                    {docType === "invoice" && workOrderCount > 0 && (
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={includeWorkOrders}
                                onChange={(e) => setIncludeWorkOrders(e.target.checked)}
                                className="h-4 w-4"
                            />
                            <span>
                                Include completed work order{workOrderCount === 1 ? "" : "s"} ({workOrderCount})
                            </span>
                            <span className="text-xs text-foreground/60">— this send only</span>
                        </label>
                    )}

                    {attachmentNote && (
                        <p className="flex items-center gap-1.5 text-xs text-foreground/60">
                            <Paperclip className="h-3.5 w-3.5" /> {attachmentNote}
                        </p>
                    )}

                    {/* TAX ENGINE V1 amber interstitial: the server refused the
                        send because the customer's tax status was never set.
                        One click sets it and continues the send. */}
                    {taxPrompt && (
                        <div className="space-y-2 rounded-md border border-amber-400/60 bg-amber-50 p-3 dark:border-amber-500/50 dark:bg-amber-950/40">
                            <p className="flex items-start gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                Tax status not set — is this customer taxable or exempt?
                            </p>
                            <p className="text-xs text-amber-800/90 dark:text-amber-300/90">
                                Picking one updates the customer, recalculates this {label.toLowerCase()}
                                &apos;s tax, and sends it in one step.
                            </p>
                            <div className="flex flex-wrap gap-2 pt-1">
                                <Button
                                    type="button"
                                    size="sm"
                                    className="h-8 bg-amber-700 text-white hover:bg-amber-800"
                                    disabled={taxResolving !== null || sendMut.isPending}
                                    onClick={() => resolveTaxAndSend("taxable")}
                                >
                                    {taxResolving === "taxable" && (
                                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                                    )}
                                    Taxable — apply tax &amp; send
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-8 border-amber-500/60"
                                    disabled={taxResolving !== null || sendMut.isPending}
                                    onClick={() => resolveTaxAndSend("exempt")}
                                >
                                    {taxResolving === "exempt" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                                    Tax exempt — send as-is
                                </Button>
                            </div>
                        </div>
                    )}

                    <BudgetImpactLine
                        estimateId={docType === "estimate" ? id : null}
                        invoiceId={docType === "invoice" ? id : null}
                    />

                    <div className="flex items-center justify-end gap-2 border-t pt-3">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={() => sendMut.mutate(undefined)}
                            disabled={
                                sendMut.isPending || taxResolving !== null || recipients.length === 0 || !subject.trim()
                            }
                        >
                            {sendMut.isPending ? (
                                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                            ) : (
                                <Mail className="mr-1 h-4 w-4" />
                            )}
                            Send {label}
                        </Button>
                    </div>
                </div>
            )}
        </Dialog>
    );
}
